import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { v4 as uuidv4 } from 'uuid'

export interface Transaction {
  id: string
  type: 'send' | 'receive' | 'airdrop_claim'
  blockchain: 'ethereum' | 'solana'
  token: string
  amount: string
  recipient?: string
  sender?: string
  reason?: string
  status: 'pending' | 'completed' | 'failed'
  hash?: string
  timestamp: number
  voiceCommand?: string
}

interface TransactionState {
  transactions: Transaction[]
  pendingTransaction: Transaction | null
  isProcessing: boolean
}

interface TransactionContextType extends TransactionState {
  addTransaction: (transaction: Omit<Transaction, 'id' | 'timestamp'>) => string
  updateTransaction: (id: string, updates: Partial<Transaction>) => void
  setPendingTransaction: (transaction: Transaction | null) => void
  getTransactionHistory: (limit?: number) => Transaction[]
  clearHistory: () => void
  clearTransactions: () => void
  exportHistory: () => string
  importHistory: (data: string) => void
  exportTransactions: () => string
  importTransactions: (data: string) => void
}

const TransactionContext = createContext<TransactionContextType | undefined>(undefined)

export const useTransaction = () => {
  const context = useContext(TransactionContext)
  if (!context) {
    throw new Error('useTransaction must be used within a TransactionProvider')
  }
  return context
}

interface TransactionProviderProps {
  children: ReactNode
}

export const TransactionProvider: React.FC<TransactionProviderProps> = ({ children }) => {
  const [transactionState, setTransactionState] = useState<TransactionState>({
    transactions: [],
    pendingTransaction: null,
    isProcessing: false
  })

  // Load transactions from localStorage on mount
  useEffect(() => {
    const savedTransactions = localStorage.getItem('voicepay_transactions')
    if (savedTransactions) {
      try {
        const transactions = JSON.parse(savedTransactions)
        setTransactionState(prev => ({ ...prev, transactions }))
      } catch (error) {
        console.error('Failed to load transaction history:', error)
      }
    }
  }, [])

  // Save transactions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('voicepay_transactions', JSON.stringify(transactionState.transactions))
  }, [transactionState.transactions])

  const addTransaction = (transaction: Omit<Transaction, 'id' | 'timestamp'>): string => {
    const newTransaction: Transaction = {
      ...transaction,
      id: uuidv4(),
      timestamp: Date.now()
    }

    setTransactionState(prev => ({
      ...prev,
      transactions: [newTransaction, ...prev.transactions]
    }))

    return newTransaction.id
  }

  const updateTransaction = (id: string, updates: Partial<Transaction>) => {
    setTransactionState(prev => ({
      ...prev,
      transactions: prev.transactions.map(tx => 
        tx.id === id ? { ...tx, ...updates } : tx
      )
    }))
  }

  const setPendingTransaction = (transaction: Transaction | null) => {
    setTransactionState(prev => ({
      ...prev,
      pendingTransaction: transaction,
      isProcessing: transaction !== null
    }))
  }

  const getTransactionHistory = (limit?: number): Transaction[] => {
    const sorted = [...transactionState.transactions].sort((a, b) => b.timestamp - a.timestamp)
    return limit ? sorted.slice(0, limit) : sorted
  }

  const clearHistory = () => {
    setTransactionState(prev => ({ ...prev, transactions: [] }))
    localStorage.removeItem('voicepay_transactions')
  }

  const exportHistory = (): string => {
    const exportData = {
      transactions: transactionState.transactions,
      exportDate: new Date().toISOString(),
      version: '1.0'
    }
    return JSON.stringify(exportData, null, 2)
  }

  const importHistory = (data: string) => {
    try {
      const importData = JSON.parse(data)
      if (importData.transactions && Array.isArray(importData.transactions)) {
        setTransactionState(prev => ({
          ...prev,
          transactions: importData.transactions
        }))
      } else {
        throw new Error('Invalid import data format')
      }
    } catch (error) {
      console.error('Failed to import transaction history:', error)
      throw new Error('Failed to import transaction history. Please check the file format.')
    }
  }

  // Alias methods for compatibility
  const clearTransactions = clearHistory
  const exportTransactions = exportHistory
  const importTransactions = importHistory

  const value: TransactionContextType = {
    ...transactionState,
    addTransaction,
    updateTransaction,
    setPendingTransaction,
    getTransactionHistory,
    clearHistory,
    clearTransactions,
    exportHistory,
    importHistory,
    exportTransactions,
    importTransactions
  }

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  )
}